import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { appsAPI } from '../services/api';
import './Applications.css';

const Applications = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingApp, setEditingApp] = useState(null);
  const [formData, setFormData] = useState({ name: '', base_url: '', description: '' });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchApps();
  }, []);

  const fetchApps = async () => {
    try {
      const response = await appsAPI.list();
      setApps(response.data);
    } catch (error) {
      console.error('Failed to fetch apps:', error);
    } finally {
      setLoading(false);
    }
  };

  const openCreateModal = () => {
    setFormData({ name: '', base_url: '', description: '' });
    setEditingApp(null);
    setError('');
    setShowModal(true);
  };

  const openEditModal = (app) => {
    setFormData({
      name: app.name,
      base_url: app.base_url,
      description: app.description || '',
    });
    setEditingApp(app);
    setError('');
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      if (editingApp) {
        await appsAPI.update(editingApp.id, formData);
      } else {
        await appsAPI.create(formData);
      }
      setShowModal(false);
      fetchApps();
    } catch (err) {
      setError(err.response?.data?.detail || 'Operation failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (app) => {
    if (!window.confirm(`Are you sure you want to delete "${app.name}"? This will also delete all test cases.`)) {
      return;
    }

    try {
      await appsAPI.delete(app.id);
      fetchApps();
    } catch (error) {
      console.error('Failed to delete app:', error);
      alert('Failed to delete application');
    }
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  return (
    <div className="applications-page">
      <div className="page-header">
        <div>
          <h1>Applications</h1>
          <p>Manage your applications and test cases</p>
        </div>
        <button onClick={openCreateModal} className="btn-primary">
          + New Application
        </button>
      </div>

      {apps.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📱</div>
          <h3>No Applications Yet</h3>
          <p>Create your first application to start generating Playwright tests</p>
          <button onClick={openCreateModal} className="btn-primary">
            Create Application
          </button>
        </div>
      ) : (
        <div className="apps-grid">
          {apps.map((app) => (
            <div key={app.id} className="app-card">
              <div className="app-header">
                <h3>{app.name}</h3>
                <div className="app-actions">
                  <button onClick={() => openEditModal(app)} className="btn-icon" title="Edit">
                    ✏️
                  </button>
                  <button onClick={() => handleDelete(app)} className="btn-icon delete" title="Delete">
                    🗑️
                  </button>
                </div>
              </div>
              <p className="app-url">{app.base_url}</p>
              {app.description && <p className="app-description">{app.description}</p>}
              <div className="app-footer">
                <span className="test-count">{app.test_case_count || 0} test cases</span>
                <Link to={`/applications/${app.id}/testcases`} className="btn-secondary">
                  View Tests →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingApp ? 'Edit Application' : 'New Application'}</h2>

            {error && <div className="error-message">{error}</div>}

            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Application Name</label>
                <input
                  type="text"
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., My E-commerce App"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="base_url">Base URL</label>
                <input
                  type="url"
                  id="base_url"
                  value={formData.base_url}
                  onChange={(e) => setFormData({ ...formData, base_url: e.target.value })}
                  placeholder="https://example.com"
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="description">Description (optional)</label>
                <textarea
                  id="description"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Brief description of the application"
                  rows={3}
                />
              </div>

              <div className="modal-actions">
                <button type="button" onClick={() => setShowModal(false)} className="btn-secondary">
                  Cancel
                </button>
                <button type="submit" className="btn-primary" disabled={submitting}>
                  {submitting ? 'Saving...' : editingApp ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Applications;
